import React, { useState, useEffect } from 'react';
import { MdClose, MdPerson, MdLocationOn, MdEdit } from 'react-icons/md';
import axios from 'axios';

const EditRevenueOfficerModal = ({ isOpen, onClose, officer, showPopup, fetchData, API_BASE_URL, userMmdaId, onSuccess, onError }) => {
  const [formData, setFormData] = useState({
    first_name: '',
    middle_name: '',
    last_name: '',
    email: '',
    phone: '',
    zone_id: '',
    mmda_id: '', // Add mmda_id to formData
    status: 'active',
  });
  const [zones, setZones] = useState([]);
  const [loading, setLoading] = useState(false);
  const [zonesLoading, setZonesLoading] = useState(false);

  const config = {
    headers: {
      'Authorization': 'Basic ' + btoa('Techland:Techland@24'),
      'Content-Type': 'application/json',
    },
  };

  // Fetch zones for the MMDA
  const fetchZones = async () => {
    setZonesLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/zones?userRole=MMDA&mmda_id=${userMmdaId}`, config);
      setZones(response.data);
    } catch (error) {

      showPopup('Failed to load zones', 'error');
    } finally {
      setZonesLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && officer) {
      setFormData({
        first_name: officer.first_name || '',
        middle_name: officer.middle_name || '',
        last_name: officer.last_name || '',
        email: officer.email || '',
        phone: officer.phone || '',
        zone_id: officer.zone_id || '',
        mmda_id: userMmdaId, // Set mmda_id from userMmdaId
        status: officer.status || 'active',
      });
      fetchZones();
    }
  }, [isOpen, officer]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Use the correct officer ID field
      const officerId = officer.officer_id || officer.revenue_officer_id || officer.id;
      const response = await axios.put(`${API_BASE_URL}/revenue-officers/${officerId}`, formData, config);
      
      if (response.status === 200) {
        showPopup('Revenue Officer updated successfully!', 'success');
        fetchData();
        onClose();
        if (onSuccess) {
          onSuccess('Updated', 'Revenue Officer', officer.full_name || `${officer.first_name} ${officer.last_name}`);
        }
      }
    } catch (error) {
      console.error('Error updating revenue officer:', error);
      const errorMessage = error.response?.data?.error || 'Failed to update revenue officer';
      showPopup(errorMessage, 'error');
      if (onError) {
        onError('Update', 'Revenue Officer', officer.full_name || `${officer.first_name} ${officer.last_name}`, errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      first_name: '',
      middle_name: '',
      last_name: '',
      email: '',
      phone: '',
      zone_id: '',
      mmda_id: '', // Reset mmda_id on close
      status: 'active',
    });
    onClose();
  };

  if (!isOpen || !officer) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100vw',
      height: '100vh',
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000,
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: 12,
        padding: 0,
        width: '90%',
        maxWidth: 500,
        maxHeight: '90vh',
        overflowY: 'auto',
        boxShadow: '0 10px 30px rgba(0, 0, 0, 0.3)',
        position: 'relative',
      }}>
        {/* Header */}
        <div style={{
          padding: '20px 24px',
          borderBottom: '1px solid #f0f0f0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          backgroundColor: '#f8f9fa',
          borderRadius: '12px 12px 0 0',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <MdEdit size={24} style={{ color: '#007bff' }} />
            <h3 style={{ margin: 0, color: '#333', fontSize: 18, fontWeight: '600' }}>
              Edit Revenue Officer
            </h3>
          </div>
          <button
            onClick={handleClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: 8,
              borderRadius: 4,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <MdClose size={20} style={{ color: '#666' }} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} style={{ padding: '24px' }}>
          <div style={{ display: 'grid', gap: 16 }}>
            {/* Name Fields */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <label style={{
                  display: 'block',
                  marginBottom: 6,
                  fontSize: 14,
                  fontWeight: '500',
                  color: '#333'
                }}>
                  First Name *
                </label>
                <input
                  type="text"
                  name="first_name"
                  value={formData.first_name}
                  onChange={handleInputChange}
                  required
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #ddd',
                    borderRadius: 6,
                    fontSize: 14,
                    outline: 'none',
                    transition: 'border-color 0.2s',
                    boxSizing: 'border-box'
                  }}
                  onFocus={e => e.target.style.borderColor = '#007bff'}
                  onBlur={e => e.target.style.borderColor = '#ddd'}
                />
              </div>
              <div>
                <label style={{
                  display: 'block',
                  marginBottom: 6,
                  fontSize: 14,
                  fontWeight: '500',
                  color: '#333'
                }}>
                  Last Name *
                </label>
                <input
                  type="text"
                  name="last_name"
                  value={formData.last_name}
                  onChange={handleInputChange}
                  required
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #ddd',
                    borderRadius: 6,
                    fontSize: 14,
                    outline: 'none',
                    transition: 'border-color 0.2s',
                    boxSizing: 'border-box'
                  }}
                  onFocus={e => e.target.style.borderColor = '#007bff'}
                  onBlur={e => e.target.style.borderColor = '#ddd'}
                />
              </div>
            </div>

            {/* Middle Name */}
            <div>
              <label style={{
                display: 'block',
                marginBottom: 6,
                fontSize: 14,
                fontWeight: '500',
                color: '#333'
              }}>
                Middle Name
              </label>
              <input
                type="text"
                name="middle_name"
                value={formData.middle_name}
                onChange={handleInputChange}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #ddd',
                  borderRadius: 6,
                  fontSize: 14,
                  outline: 'none',
                  transition: 'border-color 0.2s',
                  boxSizing: 'border-box'
                }}
                onFocus={e => e.target.style.borderColor = '#007bff'}
                onBlur={e => e.target.style.borderColor = '#ddd'}
              />
            </div>

            {/* Contact Information */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <label style={{
                  display: 'block',
                  marginBottom: 6,
                  fontSize: 14,
                  fontWeight: '500',
                  color: '#333'
                }}>
                  Email *
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #ddd',
                    borderRadius: 6,
                    fontSize: 14,
                    outline: 'none',
                    transition: 'border-color 0.2s',
                    boxSizing: 'border-box'
                  }}
                  onFocus={e => e.target.style.borderColor = '#007bff'}
                  onBlur={e => e.target.style.borderColor = '#ddd'}
                />
              </div>
              <div>
                <label style={{
                  display: 'block',
                  marginBottom: 6,
                  fontSize: 14,
                  fontWeight: '500',
                  color: '#333'
                }}>
                  Phone *
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  required
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #ddd',
                    borderRadius: 6,
                    fontSize: 14,
                    outline: 'none',
                    transition: 'border-color 0.2s',
                    boxSizing: 'border-box'
                  }}
                  onFocus={e => e.target.style.borderColor = '#007bff'}
                  onBlur={e => e.target.style.borderColor = '#ddd'}
                />
              </div>
            </div>

            {/* Zone Assignment */}
            <div>
              <label style={{
                display: 'block',
                marginBottom: 6,
                fontSize: 14,
                fontWeight: '500',
                color: '#333'
              }}>
                Assigned Zone *
              </label>
              <select
                name="zone_id"
                value={formData.zone_id}
                onChange={handleInputChange}
                required
                disabled={zonesLoading}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #ddd',
                  borderRadius: 6,
                  fontSize: 14,
                  outline: 'none',
                  transition: 'border-color 0.2s',
                  backgroundColor: zonesLoading ? '#f5f5f5' : 'white',
                  cursor: zonesLoading ? 'not-allowed' : 'pointer',
                  boxSizing: 'border-box'
                }}
                onFocus={e => e.target.style.borderColor = '#007bff'}
                onBlur={e => e.target.style.borderColor = '#ddd'}
              >
                <option value="">
                  {zonesLoading ? 'Loading zones...' : 'Select a zone'}
                </option>
                {zones.map(zone => (
                  <option key={zone.zone_id} value={zone.zone_id}>
                    {zone.zone_name}
                  </option>
                ))}
              </select>
            </div>

            {/* Status */}
            <div>
              <label style={{
                display: 'block',
                marginBottom: 6,
                fontSize: 14,
                fontWeight: '500',
                color: '#333'
              }}>
                Status *
              </label>
              <select
                name="status"
                value={formData.status}
                onChange={handleInputChange}
                required
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #ddd',
                  borderRadius: 6,
                  fontSize: 14,
                  outline: 'none',
                  transition: 'border-color 0.2s',
                  boxSizing: 'border-box'
                }}
                onFocus={e => e.target.style.borderColor = '#007bff'}
                onBlur={e => e.target.style.borderColor = '#ddd'}
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="suspended">Suspended</option>
              </select>
            </div>
          </div>

          {/* Action Buttons */}
          <div style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 12,
            marginTop: 24,
            paddingTop: 20,
            borderTop: '1px solid #f0f0f0'
          }}>
            <button
              type="button"
              onClick={handleClose}
              style={{
                padding: '10px 20px',
                border: '1px solid #ddd',
                borderRadius: 6,
                backgroundColor: 'white',
                color: '#666',
                cursor: 'pointer',
                fontSize: 14,
                fontWeight: '500',
                transition: 'all 0.2s'
              }}
              onMouseEnter={e => {
                e.target.style.backgroundColor = '#f8f9fa';
                e.target.style.borderColor = '#adb5bd';
              }}
              onMouseLeave={e => {
                e.target.style.backgroundColor = 'white';
                e.target.style.borderColor = '#ddd';
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || zonesLoading}
              style={{
                padding: '10px 20px',
                border: 'none',
                borderRadius: 6,
                backgroundColor: loading ? '#ccc' : '#007bff',
                color: 'white',
                cursor: loading ? 'not-allowed' : 'pointer',
                fontSize: 14,
                fontWeight: '500',
                transition: 'background-color 0.2s',
                display: 'flex',
                alignItems: 'center',
                gap: 8
              }}
              onMouseEnter={e => {
                if (!loading) e.target.style.backgroundColor = '#0056b3';
              }}
              onMouseLeave={e => {
                if (!loading) e.target.style.backgroundColor = '#007bff';
              }}
            >
              {loading ? 'Updating...' : 'Update Officer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditRevenueOfficerModal;
